import React from 'react';

import { useClassnames } from '@lumapps/classnames';
import { useDataAttributes } from '@lumapps/data-attributes';
import { mdiHelpCircleOutline } from '@lumapps/lumx/icons';
import { Button, Emphasis, IconButton, type Theme } from '@lumapps/lumx/react';
import { useRouter } from '@lumapps/router';
import { GLOBAL, useTranslate } from '@lumapps/translations';

import { PENDO_SCOPES } from '../../constants';
import './index.scss';

export interface PendoTriggerProps {
    isVisible: boolean;
    scope: PENDO_SCOPES;
    /** Whether to show the label alongside the icon */
    showLabel?: boolean;
    theme?: Theme;
}

const CLASSNAME = 'pendo-trigger';
const PendoTrigger: React.FC<PendoTriggerProps> = ({ isVisible = false, scope, theme, showLabel = false }) => {
    const { block } = useClassnames(CLASSNAME);
    const { get } = useDataAttributes('lumapps');
    const { translateKey } = useTranslate();

    /**
     * Unfortunately, on the back office on the legacy side, we are displaying the header on the DOM, which makes
     * this component appear twice on the page, once on the side nav of the back office and another time on the
     * header. In order to avoid this, we check that if we are on the back office, the only component that we render
     * is the trigger located on the side nav.
     */
    const { context = { isBackOffice: false } } = useRouter();
    const { isBackOffice } = context;
    const isLegacyHeaderScopeWhileInBackOffice = scope !== PENDO_SCOPES.SIDE_NAV && isBackOffice;

    if (!isVisible || isLegacyHeaderScopeWhileInBackOffice) {
        return null;
    }

    const resourceCenterLabel = translateKey(GLOBAL.RESOURCE_CENTER);

    // Show label variant (for footer)
    if (showLabel) {
        return (
            <Button
                className={block({ label: true })}
                emphasis={Emphasis.low}
                leftIcon={mdiHelpCircleOutline}
                theme={theme}
                {...get({ element: 'resource', action: 'center' })}
            >
                {resourceCenterLabel}
            </Button>
        );
    }

    // Icon only variant (for header)
    return (
        <IconButton
            className={block()}
            emphasis={Emphasis.low}
            icon={mdiHelpCircleOutline}
            color={theme}
            {...get({ element: 'resource', action: 'center' })}
            label={resourceCenterLabel}
        />
    );
};

export { PendoTrigger };
